/* Cart drawer: AJAX add-to-cart, quantity updates, slide-out panel.
   All dynamic content is built with textContent / DOM nodes — no innerHTML with data. */
(function () {
  'use strict';
  var drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer || !window.fetch) return;

  var panel = drawer.querySelector('[data-drawer-panel]');
  var itemsEl = drawer.querySelector('[data-drawer-items]');
  var emptyEl = drawer.querySelector('[data-drawer-empty]');
  var footEl = drawer.querySelector('[data-drawer-foot]');
  var subtotalEl = drawer.querySelector('[data-drawer-subtotal]');
  var countEl = drawer.querySelector('[data-drawer-count]');
  var moneyFormat = drawer.getAttribute('data-money-format') || '${{amount}}';
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var lastFocus = null;
  var busy = false;
  var closeTimer = null;
  var renderSeq = 0;
  var lastCart = null;

  /* ---------- Fitment helpers (mirrors garage.js logic) ---------- */
  var GARAGE_KEY = 'qjp_garage_v1';

  function fitHandle(s) {
    return String(s).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  function fitVehicleTags(v) {
    var t = [];
    if (v.year) t.push('year_' + fitHandle(v.year));
    if (v.make) t.push('make_' + fitHandle(v.make));
    if (v.model) t.push('model_' + fitHandle(v.model));
    return t;
  }
  function fitVehicleName(v) {
    return [v.year, v.make, v.model].filter(Boolean).join(' ');
  }
  function fitCollectionUrl(v) {
    return '/collections/all/' + fitVehicleTags(v).join('+');
  }
  function fitActiveVehicle() {
    try {
      var g = JSON.parse(localStorage.getItem(GARAGE_KEY) || '{}');
      if (!Array.isArray(g.vehicles) || g.active < 0 || !g.vehicles[g.active]) return null;
      return g.vehicles[g.active];
    } catch (e) { return null; }
  }
  function tagsMatchVehicle(cardTags, v) {
    var declares = { year: false, make: false, model: false };
    cardTags.forEach(function (t) {
      if (t.indexOf('year_') === 0) declares.year = true;
      if (t.indexOf('make_') === 0) declares.make = true;
      if (t.indexOf('model_') === 0) declares.model = true;
    });
    if (!declares.year && !declares.make && !declares.model) return 'universal';
    var vtags = fitVehicleTags(v);
    var selected = { year: false, make: false, model: false };
    vtags.forEach(function (t) { selected[t.split('_')[0]] = true; });
    var ok = ['year', 'make', 'model'].every(function (dim) {
      if (!declares[dim]) return true;
      if (!selected[dim]) return false;
      var want = vtags.filter(function (t) { return t.indexOf(dim + '_') === 0; })[0];
      return cardTags.indexOf(want) !== -1;
    });
    return ok ? 'fit' : 'nofit';
  }

  /* Product-tag cache: handle → { promise: Promise<string[]>, ts: number }
     Entries expire after TAG_CACHE_TTL ms so that merchant tag updates are
     eventually reflected without requiring a full page reload. */
  var tagCache = {};
  var TAG_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /* Fitment sequence counter — incremented on every applyDrawerFitment call so
     stale async callbacks from a previous vehicle selection are discarded. */
  var fitSeq = 0;
  function fetchProductTags(productHandle) {
    var entry = tagCache[productHandle];
    if (!entry || (Date.now() - entry.ts) > TAG_CACHE_TTL) {
      tagCache[productHandle] = {
        ts: Date.now(),
        promise: fetch('/products/' + encodeURIComponent(productHandle) + '.js', {
          headers: { Accept: 'application/json' }
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (prod) {
            if (!prod || !Array.isArray(prod.tags)) return [];
            return prod.tags
              .map(function (t) { return String(t).toLowerCase().trim(); })
              .filter(function (t) {
                return t.indexOf('year_') === 0 || t.indexOf('make_') === 0 || t.indexOf('model_') === 0;
              });
          })
          .catch(function () {
            // On error, remove the cache entry so the next call retries immediately
            delete tagCache[productHandle];
            return [];
          })
      };
    }
    return tagCache[productHandle].promise;
  }

  /* Insert a subtle prompt when items are in the cart but no vehicle is saved.
     Built entirely with DOM nodes — no innerHTML with data. */
  function renderVehiclePrompt(cart) {
    // Remove any existing prompt first (handles re-renders cleanly)
    var existing = itemsEl.querySelector('[data-drawer-vehicle-prompt]');
    if (existing) existing.remove();

    // Only show when there are items AND no active vehicle
    if (!cart || cart.item_count === 0 || fitActiveVehicle()) return;

    var prompt = document.createElement('div');
    prompt.className = 'cart-drawer__vehicle-prompt';
    prompt.setAttribute('data-drawer-vehicle-prompt', '');
    prompt.setAttribute('role', 'note');

    var icon = document.createElement('span');
    icon.className = 'cart-drawer__vehicle-prompt-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '🔧'; // 🔧

    var text = document.createElement('span');
    text.className = 'cart-drawer__vehicle-prompt-text';
    text.textContent = 'Select your vehicle to check part fitment';

    var link = document.createElement('a');
    link.className = 'cart-drawer__vehicle-prompt-link';

    var fitBar = document.querySelector('[data-fitment-bar]');
    if (fitBar) {
      // Fitment bar is on this page — scroll to it and close the drawer
      link.href = '#';
      link.addEventListener('click', function (e) {
        e.preventDefault();
        close();
        fitBar.scrollIntoView({ behavior: 'smooth', block: 'center' });
        fitBar.classList.add('fitment-bar--highlight');
        setTimeout(function () { fitBar.classList.remove('fitment-bar--highlight'); }, 2000);
        var yearSel = fitBar.querySelector('[data-fit-year]');
        if (yearSel) yearSel.focus({ preventScroll: true });
      });
    } else {
      // Fall back to the collections page which always has the fitment bar
      link.href = '/collections/all';
    }
    link.textContent = 'Pick a vehicle';

    prompt.appendChild(icon);
    prompt.appendChild(text);
    prompt.appendChild(link);

    // Prepend so it sits above item rows
    itemsEl.insertBefore(prompt, itemsEl.firstChild);
  }

  /* Apply fitment warnings to already-rendered drawer rows.
     Each row carries data-drawer-handle so we can look up its tags. */
  function applyDrawerFitment() {
    var seq = ++fitSeq; // snapshot — stale callbacks compare against this
    var v = fitActiveVehicle();
    var rows = itemsEl.querySelectorAll('[data-drawer-handle]');
    rows.forEach(function (row) {
      var productHandle = row.getAttribute('data-drawer-handle');
      var warn = row.querySelector('[data-cart-fitment-warning]');
      if (!warn) return;
      // Reset first so a vehicle swap never leaves a stale warning visible
      warn.hidden = true;
      warn.textContent = '';
      if (!v || !productHandle) return;
      fetchProductTags(productHandle).then(function (fitTags) {
        // Guard: discard if a newer vehicle selection has superseded this call
        if (seq !== fitSeq) return;
        // Guard: row may have been replaced by a newer render
        if (!row.isConnected) return;
        if (!fitTags.length) return; // universal product
        if (tagsMatchVehicle(fitTags, v) === 'nofit') {
          warn.appendChild(document.createTextNode('⚠ May not fit your ' + fitVehicleName(v) + '. Double-check before checkout. '));
          var link = document.createElement('a');
          link.className = 'product-fitment__link cart-fitment-warning__link';
          link.href = fitCollectionUrl(v);
          link.textContent = 'Shop parts for your ' + fitVehicleName(v);
          warn.appendChild(link);
          warn.hidden = false;
        }
      });
    });
  }

  function group(num) {
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function money(cents) {
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, token) {
      var v = cents / 100;
      switch (token) {
        case 'amount_no_decimals': return group(Math.round(v).toFixed(0));
        case 'amount_with_comma_separator': return v.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        case 'amount_no_decimals_with_comma_separator': return Math.round(v).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        default: return group(v.toFixed(2));
      }
    });
  }

  function headerCounts(count) {
    document.querySelectorAll('.header-actions a[href*="/cart"]').forEach(function (link) {
      var b = link.querySelector('b');
      if (!b) {
        // Badge element absent (e.g. theme omits it when count is 0) — create it
        // so the count appears immediately on the first add without waiting for refresh().
        b = document.createElement('b');
        link.appendChild(b);
      }
      if (count > 0) {
        b.textContent = String(count);
        b.hidden = false;
      } else {
        b.textContent = '';
        b.hidden = true;
      }
    });
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function render(cart) {
    itemsEl.textContent = '';
    var has = cart.item_count > 0;
    emptyEl.hidden = has;
    footEl.hidden = !has;
    countEl.hidden = !has;
    countEl.textContent = has ? '(' + cart.item_count + ')' : '';
    subtotalEl.textContent = money(cart.items_subtotal_price);
    headerCounts(cart.item_count);

    // Keep the Package Protection fee line pinned to the bottom of the list
    var sortedItems = cart.items.slice().sort(function (a, b) {
      var ap = a.handle === 'package-protection' ? 1 : 0;
      var bp = b.handle === 'package-protection' ? 1 : 0;
      return ap - bp;
    });
    sortedItems.forEach(function (item, i) {
      var row = el('div', 'drawer-row');
      // Store the product handle so applyDrawerFitment can fetch tags
      if (item.handle) row.setAttribute('data-drawer-handle', item.handle);

      var media = el('a', 'drawer-row__media');
      media.href = item.url;
      var img = document.createElement('img');
      if (item.image) {
        try {
          var u = new URL(item.image, window.location.origin);
          u.searchParams.set('width', '180');
          img.src = u.toString();
        } catch (err) {
          img.src = item.image;
        }
        img.alt = item.product_title;
      } else if (itemsEl.getAttribute('data-placeholder-src')) {
        // Same "image coming soon" placeholder the rest of the site uses
        img.src = itemsEl.getAttribute('data-placeholder-src');
        img.alt = 'Image coming soon';
        img.className = 'qp-placeholder';
      } else {
        img = null;
      }
      if (img) {
        img.loading = 'lazy';
        img.width = 72;
        media.appendChild(img);
      }
      row.appendChild(media);

      var body = el('div', 'drawer-row__body');
      var title = el('a', 'drawer-row__title', item.product_title);
      title.href = item.url;
      body.appendChild(title);
      if (item.variant_title && item.variant_title !== 'Default Title') {
        body.appendChild(el('div', 'drawer-row__meta', item.variant_title));
      }
      /* Custom build questionnaire answers (line item properties) */
      if (item.properties) {
        Object.keys(item.properties).forEach(function (k) {
          var v = item.properties[k];
          if (!v || k.charAt(0) === '_') return; // skip empty + private props
          body.appendChild(el('div', 'drawer-row__meta drawer-row__prop', k + ': ' + v));
        });
      }
      /* RNR 2026-09-07: when a discount is applied to the line (e.g. the
         segment-restricted WHOLESALE10 code auto-applied for trade customers),
         show struck original + final. Wholesale-tagged customers get the
         labeled Retail/Wholesale layout via window.QJPCustomer.wholesale. */
      var priceWrap = el('div', 'drawer-row__price');
      var wsCustomer = !!(window.QJPCustomer && window.QJPCustomer.wholesale);
      if (item.original_line_price > item.final_line_price) {
        var line1 = el('div', 'drawer-row__price-line');
        if (wsCustomer) line1.appendChild(el('span', 'ws-label', 'Retail: '));
        var struck = el('s', 'ws-retail', money(item.original_line_price));
        line1.appendChild(struck);
        var line2 = el('div', 'drawer-row__price-line');
        if (wsCustomer) line2.appendChild(el('span', 'ws-label', 'Wholesale: '));
        line2.appendChild(el('strong', 'ws-now', money(item.final_line_price)));
        priceWrap.appendChild(line1);
        priceWrap.appendChild(line2);
      } else {
        priceWrap.textContent = money(item.final_line_price);
      }
      body.appendChild(priceWrap);

      // Fitment warning placeholder — populated asynchronously by applyDrawerFitment
      var warn = el('div', 'cart-fitment-warning');
      warn.setAttribute('data-cart-fitment-warning', '');
      warn.setAttribute('role', 'alert');
      warn.hidden = true;
      body.appendChild(warn);

      var controls = el('div', 'drawer-row__controls');
      var minus = el('button', 'drawer-qty__btn', '−');
      minus.type = 'button';
      minus.setAttribute('aria-label', 'Decrease quantity of ' + item.product_title);
      var qty = el('span', 'drawer-qty__num', String(item.quantity));
      var plus = el('button', 'drawer-qty__btn', '+');
      plus.type = 'button';
      plus.setAttribute('aria-label', 'Increase quantity of ' + item.product_title);
      var qtyWrap = el('div', 'drawer-qty');
      qtyWrap.appendChild(minus);
      qtyWrap.appendChild(qty);
      qtyWrap.appendChild(plus);
      var remove = el('button', 'drawer-row__remove', 'Remove');
      remove.type = 'button';
      remove.setAttribute('aria-label', 'Remove ' + item.product_title);
      controls.appendChild(qtyWrap);
      controls.appendChild(remove);
      body.appendChild(controls);
      row.appendChild(body);
      itemsEl.appendChild(row);

      minus.addEventListener('click', function () { change(item.key, item.quantity - 1); });
      plus.addEventListener('click', function () { change(item.key, item.quantity + 1); });
      remove.addEventListener('click', function () { change(item.key, 0); });
    });

    // Cache cart so garage-change handler can re-show the prompt without a fetch
    lastCart = cart;
    // Show vehicle selection prompt when no garage vehicle is active
    renderVehiclePrompt(cart);
    // Apply fitment warnings after DOM is updated
    applyDrawerFitment();
    // Let companion scripts (package protection, etc.) react to fresh cart data
    try {
      window.dispatchEvent(new CustomEvent('qjp:cart-rendered', { detail: { cart: cart } }));
    } catch (err) { /* non-browser environments (tests) lack CustomEvent */ }
  }

  function renderIfCurrent(seq) {
    return function (cart) {
      if (seq === renderSeq) render(cart);
    };
  }

  function refresh() {
    var seq = ++renderSeq;
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(renderIfCurrent(seq))
      .catch(function () {});
  }

  // Exposed so companion scripts can request a re-render after their own cart mutations.
  window.QJPRefreshCartDrawer = refresh;

  function change(key, quantity) {
    if (busy) return;
    busy = true;
    drawer.classList.add('is-busy');
    var seq = ++renderSeq;
    fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('change failed');
        return r.json();
      })
      .then(renderIfCurrent(seq))
      .catch(function () { refresh(); })
      .then(function () { busy = false; drawer.classList.remove('is-busy'); });
  }

  function open() {
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    lastFocus = document.activeElement;
    // Enforce mutual exclusion: close the wishlist panel if it is open so
    // that both overlays can never render simultaneously.
    var openWishPanel = document.querySelector('[data-wish-panel]:not([hidden])');
    if (openWishPanel) {
      openWishPanel.hidden = true;
      var wt = document.querySelector('[data-wish-toggle]');
      if (wt) wt.setAttribute('aria-expanded', 'false');
    }
    drawer.hidden = false;
    requestAnimationFrame(function () { drawer.classList.add('is-open'); });
    document.body.classList.add('drawer-open');
    panel.focus();
  }

  function close() {
    drawer.classList.remove('is-open');
    document.body.classList.remove('drawer-open');
    if (closeTimer) clearTimeout(closeTimer);
    closeTimer = setTimeout(function () {
      drawer.hidden = true;
      closeTimer = null;
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }, 250);
  }

  // Keep keyboard focus inside the drawer while it is open.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || drawer.hidden) return;
    var focusables = panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  });

  drawer.querySelectorAll('[data-drawer-close]').forEach(function (b) {
    b.addEventListener('click', close);
  });
  drawer.querySelectorAll('[data-drawer-close-link]').forEach(function (b) {
    b.addEventListener('click', function () { close(); });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !drawer.hidden) close();
  });

  // Enforce mutual exclusion: close the drawer when the wishlist panel opens.
  // wishlist.js dispatches this event synchronously from its toggle handler
  // so the drawer begins closing before the panel animation starts.
  window.addEventListener('qjp:wishlist-opened', function () {
    if (!drawer.hidden) close();
  });

  // When a vehicle is selected while the drawer is open, refresh fitment warnings
  // and remove the "pick a vehicle" prompt without a full re-render.
  // garage.js dispatches 'qjp:garage-changed' after every save() in the same tab;
  // the 'storage' fallback catches changes made in other tabs/windows.
  function onGarageChanged() {
    if (drawer.hidden) return;
    if (fitActiveVehicle()) {
      // Vehicle added — remove the "pick a vehicle" prompt
      var prompt = itemsEl.querySelector('[data-drawer-vehicle-prompt]');
      if (prompt) prompt.remove();
    } else {
      // Vehicle cleared — re-show the prompt if there are items in the cart.
      // If a cart mutation is currently in flight (busy=true), lastCart may be stale
      // (e.g. still shows item_count=1 while the shopper's last remove hasn't responded
      // yet). Rendering with stale data would cause a brief prompt flash that the
      // subsequent render() would immediately clear. Skip the render here: once the
      // in-flight request resolves, render() will call renderVehiclePrompt() with
      // fresh data and handle the prompt correctly.
      if (busy) {
        // no-op: render() after the in-flight response will call renderVehiclePrompt
      } else if (lastCart !== null) {
        renderVehiclePrompt(lastCart);
      } else {
        // lastCart is null — drawer was opened before the first render finished;
        // fall back to a full refresh so we have real cart data to pass.
        refresh();
      }
    }
    applyDrawerFitment();
  }
  window.addEventListener('qjp:garage-changed', onGarageChanged);
  window.addEventListener('storage', function (e) {
    if (e.key === GARAGE_KEY) onGarageChanged();
  });

  // Header cart icon opens the drawer (link still works without JS).
  document.querySelectorAll('.header-actions a[href*="/cart"]').forEach(function (link) {
    link.addEventListener('click', function (e) {
      if (document.body.classList.contains('template-cart')) return;
      e.preventDefault();
      refresh().then(open);
    });
  });

  /* Set button into loading state: dims it, disables it, and shows a spinner.
     The button's original child nodes (including any SVG icons) are moved into
     a DocumentFragment stored on the element so clearButtonLoading can restore
     them exactly, without relying on textContent alone. */
  function setButtonLoading(button) {
    button.disabled = true;
    button.classList.add('btn--loading');
    var saved = document.createDocumentFragment();
    while (button.firstChild) saved.appendChild(button.firstChild);
    button._savedContent = saved;
    var spinner = document.createElement('span');
    spinner.className = 'btn-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    button.appendChild(spinner);
    button.appendChild(document.createTextNode('Adding…'));
  }

  /* Restore button to its original DOM content (including SVG icons) and
     remove the loading state. */
  function clearButtonLoading(button) {
    button.disabled = false;
    button.classList.remove('btn--loading');
    while (button.firstChild) button.removeChild(button.firstChild);
    if (button._savedContent) {
      button.appendChild(button._savedContent);
      button._savedContent = null;
    }
  }

  // AJAX add-to-cart: delegated listener on document so forms injected after page
  // load (infinite scroll, AJAX filtering, etc.) are covered automatically.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
    e.preventDefault();
    // Clear any stale error from a previous attempt before starting a new one.
    var existingError = form.querySelector('[data-add-error]');
    if (existingError) { existingError.textContent = ''; existingError.hidden = true; }
    var button = form.querySelector('[type="submit"]');
    // Capture quantity before the async fetch so the optimistic delta is correct
    // even if the DOM changes while the request is in flight.
    var qtyInput = form.querySelector('[name="quantity"]');
    var addedQty = Math.max(1, parseInt(qtyInput ? qtyInput.value : '1', 10) || 1);
    if (button) { setButtonLoading(button); }
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw new Error(d.description || 'Could not add to cart.'); });
        return r.json();
      })
      .then(function () {
        // Clear any error left from a previous failed attempt — the add succeeded.
        var errEl = form.querySelector('[data-add-error]');
        if (errEl) { errEl.textContent = ''; errEl.hidden = true; }
        // Optimistic badge increment: update the header cart count the instant the
        // add succeeds so shoppers see a response immediately, before cart.js resolves.
        // Uses the actual submitted quantity so multi-unit adds are correct.
        headerCounts(
          Math.max(0, parseInt(
            (document.querySelector('.header-actions a[href*="/cart"] b') || {}).textContent || '0',
            10
          ) || 0) + addedQty
        );
        return refresh();
      })
      .then(open)
      .catch(function (err) {
        // Guard: if the form was removed from the DOM while the request was in
        // flight (e.g. a theme page-reload on error, infinite-scroll swap, etc.)
        // skip all DOM mutations so we don't append nodes to a detached element.
        if (!form.isConnected) return;
        var status = form.querySelector('[data-add-error]');
        if (!status) {
          status = document.createElement('p');
          status.className = 'drawer-add-error';
          status.setAttribute('data-add-error', '');
          status.setAttribute('role', 'alert');
          form.appendChild(status);
        }
        status.textContent = err.message;
      })
      .then(function () {
        // Guard: button reference becomes stale if the form was removed mid-flight.
        if (button && form.isConnected) { clearButtonLoading(button); }
      });
  });
})();
